import { useEffect } from 'react'
import { useBlocker } from '@tanstack/react-router'

import { useEditorStore } from '@/features/editor/stores/editor-store'
import { selectIsDirty } from '@/features/editor/stores/editor-selectors'

export function useUnsavedChangesGuard() {
  /**
   * 站内路由拦截
   * - 必须用 getState()，避免闭包拿到旧值
   */
  const blocker = useBlocker({
    withResolver: true as const,
    enableBeforeUnload: false, // 浏览器卸载我们自己处理
    shouldBlockFn: () => {
      const s = useEditorStore.getState()
      return selectIsDirty(s)
    },
  })

  const { status } = blocker

  /**
   * 浏览器刷新 / 关闭标签页
   * - 只注册一次
   * - 运行时读取 store 最新值
   */
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const s = useEditorStore.getState()
      if (!selectIsDirty(s)) return

      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  /**
   * 保存成功后，如果之前被 blocker 卡住，自动放行
   * - 防止「已保存但还卡在 blocked 状态」
   */
  useEffect(() => {
    if (status !== 'blocked') return

    const s = useEditorStore.getState()
    if (!selectIsDirty(s)) {
      blocker.proceed()
    }
  }, [status, blocker])

  return {
    /** 是否显示确认弹窗 */
    confirmOpen: status === 'blocked',

    /** 留在当前页面 */
    stay: () => {
      if (status === 'blocked') {
        blocker.reset()
      }
    },

    /** 放弃更改并离开 */
    leave: () => {
      if (status === 'blocked') {
        blocker.proceed()
      }
    },
  }
}
